<template>
  <v-card class="register-container">
    <v-btn
      variant="plain"
      class="return-btn"
      size="small"
      prepend-icon="mdi-chevron-left"
      :to="{ name: 'Login' }"
      :ripple="false"
    >
      {{ $t('COMMON.BUTTON.BACK') }}
    </v-btn>
    <v-container class="logo-container">
      <img alt="logo Boomerang" class="logo" src="@/assets/images/boomerang_black_logo.png" />
    </v-container>
    <v-spacer></v-spacer>
    <v-container>
      <v-form v-model="isFormValid" @submit.prevent="submitForm">
        <h1 class="title">{{ $t('LOGIN_REGISTER.TITLE.HEADER') }}</h1>

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.LASTNAME') }}</p>
        <v-text-field v-model="lastname" :rules="requiredRules" />

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.FIRSTNAME') }}</p>
        <v-text-field v-model="firstname" :rules="requiredRules" />

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.EMAIL') }}</p>
        <v-text-field type="email" v-model="email" :rules="[...requiredRules, ...emailRules]" />

        <p data-custom="true" class="subtitle">
          {{ $t('LOGIN_REGISTER.TITLE.PHONE_NUMBER.TITLE') }}
        </p>
        <v-phone-input
          v-model="phoneNumber"
          v-model:country="selectedCountry"
          :includeCountries="allCountries"
          :rules="requiredRules"
        />

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.COMPANY_NAME') }}</p>
        <v-text-field v-model="networkName" :rules="requiredRules" />

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.USER_JOB') }}</p>
        <v-select
          id="select-job"
          :multiple="false"
          :items="jobs"
          item-title="key"
          item-value="value"
          :rules="requiredRules"
          :no-data-text="$t('LOGIN_REGISTER.MESSAGE.NO_DATA')"
          v-model="selectedJob"
        >
          <template v-slot:selection="{ item }">
            <v-chip size="small">
              <span>{{ item.title }}</span>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item :title="item.title" :value="item.value" v-bind="props">
              <v-tooltip
                v-if="shouldShowTooltip(item, 'select-job')"
                activator="parent"
                location="right"
              >
                {{ item.title }}
              </v-tooltip>
            </v-list-item>
          </template>
        </v-select>

        <p data-custom="true" v-if="storeRequest" class="subtitle">
          {{ $t('LOGIN_REGISTER.TITLE.WHICH_MARKET') }}
        </p>
        <v-text-field v-if="storeRequest" v-model="establishmentName" :rules="requiredRules" />

        <p data-custom="true" v-if="jobDetails" class="subtitle">
          {{ $t('LOGIN_REGISTER.TITLE.JOB_DETAILS') }}
        </p>
        <v-text-field v-if="jobDetails" v-model="jobDescription" :rules="requiredRules" />

        <p data-custom="true" class="subtitle">{{ $t('LOGIN_REGISTER.TITLE.GEOGRAPHICAL') }}</p>
        <v-select
          id="select-zone"
          v-model="selectedZone"
          item-title="title"
          item-value="technical_id"
          multiple
          :items="zones"
          :rules="[...requiredRules, ...requiredMultipleRules]"
          :no-data-text="$t('LOGIN_REGISTER.MESSAGE.NO_DATA')"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip size="small" v-if="index < 1">
              <span>{{ item.title }}</span>
            </v-chip>
            <span v-if="index === 1" class="text-grey text-caption align-self-center">
              (+{{ selectedZone.length - 1 }}
              {{
                selectedZone.length - 1 === 1
                  ? $t('LOGIN_REGISTER.TITLE.CHIP_MESSAGE')
                  : $t('LOGIN_REGISTER.TITLE.CHIPS_MESSAGE')
              }})
            </span>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item :title="item.title" :value="item.value" v-bind="props">
              <v-tooltip
                v-if="shouldShowTooltip(item, 'select-zone')"
                activator="parent"
                location="right"
              >
                {{ item.title }}
              </v-tooltip>
            </v-list-item>
          </template>
        </v-select>

        <div class="rows">
          <div>
            {{ $t('LOGIN_REGISTER.TITLE.QUESTION_CGU') }}
            <a class="cgu" @click="openCguPage()">
              {{ $t('LOGIN_REGISTER.TITLE.CGU') }}
            </a>
          </div>
          <v-checkbox hide-details v-model="tosAccepted" />
        </div>

        <v-btn
          class="boom-button"
          variant="outlined"
          type="submit"
          :disabled="loading || !isFormValid"
          :loading="loading"
        >
          {{ $t('LOGIN_REGISTER.BUTTON.SUBMIT') }}
        </v-btn>
      </v-form>
    </v-container>
  </v-card>
</template>

<script>
import router from '@/router'
import { VPhoneInput } from 'v-phone-input'
import Validator from '@/components/utils/validator'
import RegionCodeService from '@/services/RegionCodeService'
import {
  displayToast,
  handleResponseError,
  shouldShowTooltip
} from '@/components/utils/globalFunction'

const jobEnum = Object.freeze({
  STORE_SALESMAN_JOB: 'store_salesman_job',
  TELEPHONE_SALESMAN_JOB: 'telephone_salesman_job',
  STORE_MANAGER_JOB: 'store_manager_job',
  OTHER: 'other_job'
})
export default {
  name: 'RegisterPage',
  components: { VPhoneInput },
  data() {
    return {
      selectedCountry: null,
      loading: false,
      isFormValid: false,
      storeRequest: false,
      jobDetails: false,
      tosAccepted: false,
      showModal: false,
      lastname: '',
      firstname: '',
      email: '',
      phoneNumber: '',
      networkName: '',
      establishmentName: '',
      jobDescription: '',
      selectedZone: [],
      zones: [],
      selectedJob: null,
      jobs: [],
      allCountries: [],

      requiredRules: Validator.requiredRules,
      requiredMultipleRules: Validator.requiredMultipleRules,
      emailRules: Validator.emailRules
    }
  },
  async created() {
    this.jobs = [
      {
        key: this.$t('JOB_LIST.STORE_SALESMAN_JOB'),
        value: 'store_salesman_job'
      },
      {
        key: this.$t('JOB_LIST.TELEPHONE_SALESMAN_JOB'),
        value: 'telephone_salesman_job'
      },
      {
        key: this.$t('JOB_LIST.TRAVELING_SALESMAN_JOB'),
        value: 'traveling_salesman_job'
      },
      {
        key: this.$t('JOB_LIST.STORE_MANAGER_JOB'),
        value: 'store_manager_job'
      },
      {
        key: this.$t('JOB_LIST.GEOGRAPHIC_MANAGER_JOB'),
        value: 'geographic_manager_job'
      },
      {
        key: this.$t('JOB_LIST.RSE_DEPARTMENT_JOB'),
        value: 'rse_department_job'
      },
      {
        key: this.$t('JOB_LIST.MARKETING_DEPARTMENT_JOB'),
        value: 'marketing_department_job'
      },
      {
        key: this.$t('JOB_LIST.PURCHASING_DEPARTMENT_JOB'),
        value: 'purchasing_department_job'
      },
      {
        key: this.$t('JOB_LIST.OTHER_JOB'),
        value: 'other_job'
      }
    ]

    try {
      const response = await RegionCodeService.getRegionCodeList()
      this.zones = Object.values(response.item_list).map((option) => ({
        technical_id: option.technical_id,
        title: ` ${this.reformatData(option.region)} ${this.reformatData(option.country)}`
      }))

      this.allCountries = this.getCountryCode(response.item_list)
    } catch (error) {
      handleResponseError(error)
    }
  },
  watch: {
    selectedJob(selected) {
      if (
        selected === jobEnum.STORE_SALESMAN_JOB ||
        selected === jobEnum.TELEPHONE_SALESMAN_JOB ||
        selected === jobEnum.STORE_MANAGER_JOB
      ) {
        this.storeRequest = true
        this.jobDetails = false
      } else if (selected === jobEnum.OTHER) {
        this.jobDetails = true
        this.storeRequest = false
      } else {
        this.storeRequest = false
        this.jobDetails = false
      }
    }
  },
  methods: {
    shouldShowTooltip,
    reformatData(string) {
      const lowercaseString = string.toLowerCase()
      const trimmedString = lowercaseString.trim()
      const normalizedString = trimmedString.replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ')

      return normalizedString.replace(/(?:^|\s)(\p{Ll})/gu, function (match) {
        return match.toUpperCase()
      })
    },
    async submitForm() {
      await this.$recaptchaLoaded()
      const captchaToken = await this.$recaptcha('login')
      this.loading = true
      const userData = {
        firstname: this.firstname.trim(),
        lastname: this.lastname.trim(),
        email: this.email.trim(),
        phone_number: this.phoneNumber.trim(),
        network_name: this.networkName.trim(),
        is_establishment: this.storeRequest,
        establishment_name: this.establishmentName.trim(),
        is_custom_job: this.jobDetails,
        job_description: this.jobDescription,
        region_code_id_list: this.selectedZone,
        job_list: [this.selectedJob],
        captcha_token: captchaToken
      }
      if (this.isFormValid) {
        try {
          await this.$store.dispatch('auth/register', userData)
          displayToast(this.$t('LOGIN_REGISTER.MESSAGE.SUCCESS'), 'success')
          await router.push({ path: 'login' })
        } catch (error) {
          if (error.response) {
            if (
              error.response &&
              error.response.data &&
              error.response.data.key === 'invalid_captcha_token'
            ) {
              displayToast(this.$t('LOGIN_REGISTER.MESSAGE.ERROR.CAPTCHA'), 'error')
            }
            if (
              error.response &&
              error.response.data &&
              error.response.data.key === 'invalid_job_type_error_key'
            ) {
              displayToast(this.$t('LOGIN_REGISTER.MESSAGE.ERROR.JOB_TYPE'), 'error')
            }
            if (error.response.status === 502) {
              displayToast(this.$t('ERROR_STATUS_CODE_MESSAGE.502'), 'error')
            }
          } else {
            displayToast(error, 'error')
          }
        } finally {
          this.loading = false
        }
      }
    },
    getCountryCode(zones) {
      return zones.map((item) => {
        return item.country_code
      })
    },
    openCguPage() {
      window.open('/BOOMERANG-CGU-fevrier-2024.pdf', '_blank')
    }
  }
}
</script>
<style scoped>
:deep(.v-select .v-field--dirty .v-select__selection) {
  color: black !important;
}

.rows {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.return {
  margin: auto;
  display: flex;
  justify-content: end;
}

.logo-container {
  margin-right: auto;
  margin-left: auto;
  margin-top: 50px;
  padding: unset;
}

.cgu {
  margin-left: 5px;
  cursor: pointer;
  color: #559a1b;
  text-decoration: unset;
}

.link:hover {
  color: #559a1b;
}

:deep(.v-field) {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selector {
  margin: 2% 0 8% 0;
}

.subtitle {
  line-height: 20px;
  text-align: left;
  margin-bottom: 2%;
}

.title {
  text-align: center;
  font-size: 150%;
  font-style: normal;
  padding-bottom: 10%;
}

.boom-button {
  width: 100%;
}

.register-container {
  overflow-y: auto;
  border-radius: 20px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: auto 5%;
  padding: 21.219px 29.703px;
}

@media (max-width: 499px) {
  .register-container {
    width: 90vw;
    height: 95vh;
  }
}

@media (min-width: 500px) and (max-width: 767px) {
  .register-container {
    width: 70vw;
    height: 95vh;
  }
}

@media (min-width: 768px) and (max-width: 1099px) {
  .register-container {
    width: 40vw;
    height: 80vh;
  }
}

@media (min-width: 1100px) and (max-width: 1599px) {
  .register-container {
    width: 30vw;
    height: 80vh;
  }
}

@media (min-width: 1600px) {
  .register-container {
    width: 20vw;
    height: 80vh;
  }
}

:deep(.v-text-field .v-input__details) {
  padding-inline-start: unset;
  padding-inline-end: unset;
}

.logo {
  display: flex;
  flex-direction: column;
  margin: auto;
  align-items: center;
  justify-content: center;
  width: 90%;
}

.return-btn {
  display: flex;
  justify-content: start;
  position: absolute;
  top: 25px;
  left: 25px;
  color: black;
}

.v-chip.v-chip--density-default {
  color: #559a1b;
}

.return-btn:hover {
  color: #559a1b;
}
</style>
